import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { IS_DEV_MODE } from "@/services/api";
import { normalizePhoneForDB } from "@/lib/phone";

/**
 * Fetches the latest dev OTP for the given phone number (non-production only).
 *
 * @param phone - The phone number to look up. Pass `null` to disable.
 * @param trigger - Increment this value to re-fetch (e.g. after sending OTP).
 * @returns The OTP string or null.
 */
export function useDevOtp(phone: string | null, trigger: number = 0): string | null {
  const [devOtp, setDevOtp] = useState<string | null>(null);

  useEffect(() => {
    if (!IS_DEV_MODE || !phone || trigger === 0) {
      setDevOtp(null);
      return;
    }

    // dev_otps stores phones in canonical no-'+' form (matches GoTrue and the
    // send-sms-hook write path, and now also the Nest D1 requestOtp path in
    // non-production). Normalize here so callers can pass either
    // '+966...' or '966...' transparently.
    // Non-Saudi numbers (public interest capture) aren't canonicalised by
    // normalizePhoneForDB — the API writes them as E.164 digits without "+".
    const lookupPhone = normalizePhoneForDB(phone) ?? phone.replace(/^\+/, "");
    setDevOtp(null);
    const timer = setTimeout(async () => {
      const { data: otpRow } = await supabase
        .from("dev_otps")
        .select("otp")
        .eq("phone", lookupPhone)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (otpRow?.otp) {
        setDevOtp(otpRow.otp);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [phone, trigger]);

  return devOtp;
}
